import AsyncStorage from '@react-native-async-storage/async-storage';
import { create } from 'zustand';
import { ONBOARDING_VERSION } from '@/lib/onboardingVersion';
// `import type` de propósito: o Babel elide o require e o ciclo
// `lib/i18n` → este store → `lib/onboarding` → `lib/i18n` não se fecha.
import type {
    FirstStepKey,
    OnboardingDraft,
    OnboardingGoal,
    OnboardingStartMode,
} from '@/lib/onboarding';

interface AppState {
    onboardingCompleted: boolean;
    onboardingVersion: number;
    onboardingDraft: OnboardingDraft | null;
    selectedGoals: OnboardingGoal[];
    startMode: OnboardingStartMode | null;
    /** "Ocultar" no card de Primeiros passos da Home (ADR 0071). Zera ao concluir o onboarding. */
    checklistDismissed: boolean;
    /**
     * O checklist de Primeiros passos já foi EXIBIDO INCOMPLETO nesta instalação? Guarda de
     * upgrade (ADR 0071): o evento `first_steps_complete` e a celebração só disparam depois
     * disso — senão um usuário antigo que já satisfaz o critério novo receberia "parabéns" no
     * dia do upgrade. Instalação nova sempre passa por aqui (o 3º passo nasce pendente).
     */
    firstStepsShown: boolean;
    /**
     * O checklist já foi SATISFEITO alguma vez nesta instalação. Trava definitiva: depois de
     * concluído, o card não volta mais, nem quando a entidade que concluiu um passo é apagada
     * (apagar a meta não devolve "Criar sua primeira meta"). Sem isso o checklist era derivado
     * das contagens VIVAS e ressuscitava a cada exclusão — cobrança de algo que a pessoa já fez.
     */
    firstStepsCompleted: boolean;
    /** Passos do checklist que o usuário pulou ("Pular" no item). */
    firstStepsSkipped: FirstStepKey[];
    /** Timestamp (ms) do 1º boot após este recurso existir — base do "tempo desde o 1º uso". */
    firstOpenAt: number | null;
    /**
     * GRANDFATHERING do hard paywall iOS (design 4g, ADR 0047): quem já usa o Kontto grátis
     * no iOS não pode ser trancado pra fora dos próprios dados. Derivada UMA vez na
     * hidratação: instalação persistida sem esta flag e com onboarding já concluído =
     * usuário existente → isento pra sempre (segue no freemium, como o Android).
     * Instalações novas nascem `false` e caem no gate. Reinstalar zera o AsyncStorage e o
     * usuário volta como novo — aceitável (é o padrão do mercado pra grandfathering local).
     *
     * A janela freemium (1.0.27..1.0.38, ADR 0042) removeu a flag da persistência, então
     * TODO iOS que abriu o app nesse período gravou o estado sem ela — é justamente o que
     * faz a derivação abaixo isentá-los na volta do gate. Ver docs/adr/0047.
     */
    hardPaywallExempt: boolean;
    /**
     * Já mostramos o fluxo de ATIVAÇÃO da 1ª transação (vai direto pro trial)?
     * Dispara uma única vez por instalação, quando a 1ª transação real é salva (no
     * onboarding OU na Home). Persistido pra nunca repetir. Ver
     * `maybeShowFirstTransactionTrial` em lib/pro/proPrompts.ts.
     */
    hasSeenActivationValueMoment: boolean;
    /**
     * Quando a 1ª transação da instalação foi salva (epoch ms), ou `null`. Âncora do nudge
     * do 2º lançamento (ADR 0083) — o D+1 conta a partir do PRIMEIRO GASTO, não do
     * `firstOpenAt`: quem instala às 23h e só lança no dia seguinte não pode ser cutucado
     * duas horas depois de ter registrado. Nasce `null` pra toda a base instalada, e quem
     * já tem transações nunca fica elegível (a guarda é `transactionCount === 1`).
     */
    firstTransactionAt: number | null;
    hydrated: boolean;

    setOnboardingCompleted: (completed: boolean) => void;
    saveOnboardingDraft: (draft: OnboardingDraft | null) => void;
    completeOnboarding: (draft?: OnboardingDraft | null) => void;
    resetOnboarding: () => void;
    setChecklistDismissed: (dismissed: boolean) => void;
    markFirstStepsShown: () => void;
    /** Trava o checklist como concluído (idempotente). */
    markFirstStepsCompleted: () => void;
    /** Destrava o checklist e zera os "Pular" (Ajustes › Manutenção). Não toca em dado. */
    reopenFirstSteps: () => void;
    skipFirstStep: (key: FirstStepKey) => void;
    setHasSeenActivationValueMoment: (seen: boolean) => void;
    /** Carimba a 1ª transação (uma vez — chamadas seguintes são no-op). */
    markFirstTransactionAt: (at: number) => void;
    hydrate: () => Promise<void>;
}

type PersistedAppState = Pick<
    AppState,
    | 'onboardingCompleted'
    | 'onboardingVersion'
    | 'onboardingDraft'
    | 'selectedGoals'
    | 'startMode'
    | 'checklistDismissed'
    | 'firstStepsShown'
    | 'firstStepsCompleted'
    | 'firstStepsSkipped'
    | 'firstOpenAt'
    | 'hardPaywallExempt'
    | 'hasSeenActivationValueMoment'
    | 'firstTransactionAt'
>;

const STORAGE_KEY = '@finapp_app_store';

const defaultPersistedState: PersistedAppState = {
    onboardingCompleted: false,
    onboardingVersion: ONBOARDING_VERSION,
    onboardingDraft: null,
    selectedGoals: [],
    startMode: null,
    checklistDismissed: false,
    firstStepsShown: false,
    firstStepsCompleted: false,
    firstStepsSkipped: [],
    firstOpenAt: null,
    hardPaywallExempt: false,
    hasSeenActivationValueMoment: false,
    firstTransactionAt: null,
};

function getPersistedState(state: AppState): PersistedAppState {
    return {
        onboardingCompleted: state.onboardingCompleted,
        onboardingVersion: state.onboardingVersion,
        onboardingDraft: state.onboardingDraft,
        selectedGoals: state.selectedGoals,
        startMode: state.startMode,
        checklistDismissed: state.checklistDismissed,
        firstStepsShown: state.firstStepsShown,
        firstStepsCompleted: state.firstStepsCompleted,
        firstStepsSkipped: state.firstStepsSkipped,
        firstOpenAt: state.firstOpenAt,
        hardPaywallExempt: state.hardPaywallExempt,
        hasSeenActivationValueMoment: state.hasSeenActivationValueMoment,
        firstTransactionAt: state.firstTransactionAt,
    };
}

function persistState(state: AppState) {
    AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(getPersistedState(state))).catch(() => {});
}

/**
 * O onboarding concluído que ficou GRAVADO neste aparelho, lido direto do AsyncStorage: o boot
 * decide o cofre da instalação anterior (ADR 0202) em paralelo com o `hydrate`. Lança se o
 * registro existe e não abre, porque ali "não sei" não pode virar "instalação nova".
 */
export async function readPersistedOnboardingCompleted(): Promise<boolean> {
    const stored = await AsyncStorage.getItem(STORAGE_KEY);
    if (!stored) return false;
    const parsed = JSON.parse(stored) as Partial<PersistedAppState> | null;
    if (!parsed || typeof parsed !== 'object') throw new Error('registro do app ilegível');
    return parsed.onboardingCompleted === true;
}

function areGoalsEqual(current: OnboardingGoal[], next: OnboardingGoal[]) {
    return current.length === next.length && current.every((item, index) => item === next[index]);
}

export const useAppStore = create<AppState>((set, get) => ({
    ...defaultPersistedState,
    hydrated: false,

    setOnboardingCompleted: (completed) => {
        set({ onboardingCompleted: completed });
        persistState(get());
    },

    saveOnboardingDraft: (draft) => {
        const current = get();
        const nextSelectedGoals = draft?.goals ?? [];
        const nextStartMode = draft?.startMode ?? null;

        if (
            current.onboardingDraft === draft
            && current.startMode === nextStartMode
            && areGoalsEqual(current.selectedGoals, nextSelectedGoals)
        ) {
            return;
        }

        set({
            onboardingDraft: draft,
            selectedGoals: nextSelectedGoals,
            startMode: nextStartMode,
        });
        persistState(get());
    },

    completeOnboarding: (draft) => {
        set((state) => ({
            onboardingCompleted: true,
            onboardingVersion: ONBOARDING_VERSION,
            onboardingDraft: draft ?? state.onboardingDraft,
            selectedGoals: draft?.goals ?? state.selectedGoals,
            startMode: draft?.startMode ?? state.startMode,
            checklistDismissed: false,
            firstStepsCompleted: false,
            firstStepsSkipped: [],
        }));
        persistState(get());
    },

    resetOnboarding: () => {
        set({
            ...defaultPersistedState,
        });
        persistState(get());
    },

    setChecklistDismissed: (dismissed) => {
        set({ checklistDismissed: dismissed });
        persistState(get());
    },

    markFirstStepsShown: () => {
        if (get().firstStepsShown) {
            return;
        }
        set({ firstStepsShown: true });
        persistState(get());
    },

    markFirstStepsCompleted: () => {
        if (get().firstStepsCompleted) {
            return;
        }
        set({ firstStepsCompleted: true });
        persistState(get());
    },

    reopenFirstSteps: () => {
        // Zera as TRÊS formas de sumir com o card (trava de conclusão, "Ocultar" e os
        // "Pular"), e re-arma a guarda de upgrade pra ele voltar a valer como "exibido
        // incompleto". Os passos em si continuam vindo das contagens reais: se tudo já
        // estiver de fato feito, o card segue escondido — não há o que refazer.
        set({
            firstStepsCompleted: false,
            checklistDismissed: false,
            firstStepsSkipped: [],
            firstStepsShown: false,
        });
        persistState(get());
    },

    skipFirstStep: (key) => {
        if (get().firstStepsSkipped.includes(key)) {
            return;
        }
        set((state) => ({ firstStepsSkipped: [...state.firstStepsSkipped, key] }));
        persistState(get());
    },

    markFirstTransactionAt: (at) => {
        // Uma vez só: se já existe carimbo, a "1ª transação" já aconteceu. Regravar moveria
        // a âncora do D+1 a cada lançamento e o nudge nunca chegaria.
        if (get().firstTransactionAt !== null) {
            return;
        }
        set({ firstTransactionAt: at });
        persistState(get());
    },

    setHasSeenActivationValueMoment: (seen) => {
        if (get().hasSeenActivationValueMoment === seen) {
            return;
        }
        set({ hasSeenActivationValueMoment: seen });
        persistState(get());
    },

    hydrate: async () => {
        try {
            const stored = await AsyncStorage.getItem(STORAGE_KEY);
            if (!stored) {
                // 1º boot de todos: marca o início da "idade" do usuário.
                set({ firstOpenAt: Date.now(), hydrated: true });
                persistState(get());
                return;
            }

            const parsed = JSON.parse(stored) as Partial<PersistedAppState>;
            // Instalações anteriores a este recurso não têm firstOpenAt: começa a contar agora.
            const firstOpenAt = parsed.firstOpenAt ?? Date.now();
            // Migração do grandfathering: instalação persistida ANTES deste build (flag
            // ausente) que já concluiu o onboarding = usuário free existente → isento.
            const hardPaywallExempt =
                parsed.hardPaywallExempt ?? (parsed.onboardingCompleted === true);
            set({
                onboardingCompleted: parsed.onboardingCompleted ?? false,
                onboardingVersion: parsed.onboardingVersion ?? ONBOARDING_VERSION,
                onboardingDraft: parsed.onboardingDraft ?? null,
                selectedGoals:
                    parsed.selectedGoals
                    ?? parsed.onboardingDraft?.goals
                    ?? [],
                startMode:
                    parsed.startMode
                    ?? parsed.onboardingDraft?.startMode
                    ?? null,
                checklistDismissed: parsed.checklistDismissed ?? false,
                firstStepsShown: parsed.firstStepsShown ?? false,
                firstStepsCompleted: parsed.firstStepsCompleted ?? false,
                firstStepsSkipped: parsed.firstStepsSkipped ?? [],
                firstOpenAt,
                hardPaywallExempt,
                hasSeenActivationValueMoment: parsed.hasSeenActivationValueMoment ?? false,
                hydrated: true,
            });
            if (parsed.firstOpenAt == null || parsed.hardPaywallExempt == null) {
                persistState(get());
            }
        } catch {
            set({ hydrated: true });
        }
    },
}));
